import { useState, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Camera, Upload, RefreshCw, ArrowRight } from "lucide-react";

const PLACEHOLDER = "https://images.pexels.com/photos/7195900/pexels-photo-7195900.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function PhotoView({ onGoGenerate }) {
  const [preview, setPreview] = useState(null);
  const [base64, setBase64] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Use a JPEG, PNG, or WEBP image."); return;
    }
    const reader = new FileReader();
    reader.onload = () => { setPreview(reader.result); setBase64(reader.result); setResult(null); };
    reader.readAsDataURL(file);
  };

  const analyze = async () => {
    if (!base64) { toast.error("Upload a photo first."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/photo-to-copy", { image_base64: base64 });
      setResult(data);
      toast.success("Features extracted from your photo!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const useInGenerator = () => {
    const features = (result.features || []).join(", ");
    sessionStorage.setItem("penhaus_prefill", JSON.stringify({ features }));
    onGoGenerate?.();
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <h2 className="font-semibold text-lg flex items-center gap-2"><Camera className="h-5 w-5" /> Smart Photo Detection</h2>
        <p className="text-sm text-slate-500 mt-1">Upload a listing photo — AI spots premium details (granite, hardwood, crown molding…) and writes the copy.</p>
        <div onClick={() => fileRef.current?.click()} data-testid="photo-dropzone"
          className="mt-5 border-2 border-dashed border-slate-300 rounded-lg overflow-hidden cursor-pointer hover:border-slate-400 transition-colors">
          <img src={preview || PLACEHOLDER} alt="Preview" className="w-full h-64 object-cover" />
        </div>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} data-testid="photo-input" />
        <div className="flex gap-3 mt-4">
          <Button onClick={() => fileRef.current?.click()} variant="outline" className="flex-1" data-testid="photo-upload-btn">
            <Upload className="h-4 w-4 mr-2" /> {preview ? "Change photo" : "Upload photo"}
          </Button>
          <Button onClick={analyze} disabled={loading || !base64} className="flex-1 bg-slate-900 hover:bg-slate-800 text-white" data-testid="photo-analyze-btn">
            {loading ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Analyzing…</> : "Extract features"}
          </Button>
        </div>
      </div>

      <div>
        {!result && <div className="bg-white border border-dashed border-slate-300 rounded-lg p-12 text-center h-full flex flex-col items-center justify-center" data-testid="photo-empty">
          <Camera className="h-10 w-10 text-slate-300" />
          <p className="text-slate-500 mt-4">Detected room and features will appear here.</p>
        </div>}
        {result && (
          <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="photo-result">
            {result.room && <span className="inline-block bg-slate-100 text-slate-700 text-xs font-medium px-2.5 py-1 rounded-full">{result.room}</span>}
            {result.features?.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-semibold text-slate-700">Detected features</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {result.features.map((f, i) => <span key={i} className="bg-[#D4AF37]/15 text-[#8a6d10] text-sm px-2.5 py-1 rounded-md">{f}</span>)}
                </div>
              </div>
            )}
            {result.suggested_blurb && <p className="text-slate-700 mt-4 leading-relaxed text-[15px]">{result.suggested_blurb}</p>}
            <Button onClick={useInGenerator} className="mt-5 w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="photo-use-btn">
              Use these features in Generator <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
